import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './AppReview.css';

const testAccounts = [
  {
    role: 'Regular User',
    color: '#00897B',
    email: 'reviewer@safeher.xyz',
    password: 'Review2024!',
    note: 'Use this account to test map browsing, incident reporting, upvoting, and proximity alerts.',
  },
  {
    role: 'Moderator',
    color: '#F57C00',
    email: 'mod.reviewer@safeher.xyz',
    password: 'Review2024!',
    note: 'Pre-assigned to San Francisco. Use this to test report verification, hiding, and messaging reporters.',
  },
  {
    role: 'Admin',
    color: '#C62828',
    email: 'admin@safeher.xyz',
    password: 'Admin2024!',
    note: 'Full access. Use this to approve moderator applications, manage users, and view area-wide reports. Access via the web dashboard at safeher.xyz.',
  },
];

const walkthrough = [
  {
    icon: '🔐',
    title: 'Create an account or use the test credentials',
    body: 'Tap "Get Started" on the welcome screen. Register with any email, or use the pre-seeded test accounts above. Email verification is disabled for the test environment.',
  },
  {
    icon: '🗺️',
    title: 'Browse the safety map',
    body: 'After login you land on the live safety map. The map is pre-seeded with sample incidents across San Francisco so you can see color-coded severity markers immediately — no real reports required.',
  },
  {
    icon: '📍',
    title: 'Submit an incident report',
    body: 'Tap the "+" button (bottom right). Long-press any location on the map to drop a pin, or tap your current location. Select a severity level (1–3), write a brief description, and submit. By default the report is anonymous.',
  },
  {
    icon: '🔔',
    title: 'Set a proximity alert',
    body: 'Go to Profile → Alert Settings. Choose a radius (0.25–10 miles) and minimum severity. When a new report appears within that radius, you receive a push notification.',
  },
  {
    icon: '👍',
    title: 'Upvote a report',
    body: 'Tap any map marker to open the report detail sheet. Tap the upvote button. You can choose to upvote anonymously. The upvote count shows on the map marker.',
  },
  {
    icon: '🛡️',
    title: 'Moderator workflow (use mod account)',
    body: 'Log in with the Moderator test account. Tap the shield icon in the nav bar to open the Moderator Dashboard. You will see reports within the pre-assigned San Francisco area. Tap a report to verify its accuracy, hide spam or false posts, or send an anonymous message to the reporter for more context. Moderators manage information on the platform only — they do not take any physical action.',
  },
  {
    icon: '📨',
    title: 'Anonymous inbox',
    body: 'While logged in as the moderator, send a message to a report. Then switch to the Regular User account — you will find the message in Profile → Inbox. You can reply; the moderator sees only a pseudonymous ID, never your name.',
  },
  {
    icon: '📋',
    title: 'Moderator application',
    body: 'Using the Regular User account, go to Profile → Become a Moderator. Fill in your full name, a brief statement, and a coverage area. The admin account can approve or deny the application from the web dashboard.',
  },
];

const privacyPoints = [
  {
    title: 'No personally identifiable information on reports',
    body: 'When a report is submitted, it is stored with a session-derived pseudonymous ID. The reporter\'s name and email are structurally absent — not just hidden. Moderators and admins cannot link a report to an account.',
  },
  {
    title: 'Anonymous upvotes',
    body: 'Upvotes can be submitted anonymously. When anonymous, the display name shows as "Anonymous" everywhere — no exceptions.',
  },
  {
    title: 'Not an emergency service',
    body: 'SafeHer does not contact authorities on behalf of users. For serious or emergency situations, the app directs users to contact local emergency services themselves. The platform is for early community awareness, not emergency dispatch.',
  },
  {
    title: 'Moderator messages never expose identity',
    body: 'In-app messages between moderators and reporters are routed through pseudonymous IDs. No email addresses or real names are transmitted.',
  },
  {
    title: 'No background location tracking',
    body: 'Location is only read in-session for map centering and alert radius checks. The app does not store device location history.',
  },
  {
    title: 'Push notifications (proximity alerts)',
    body: 'Alerts use APNs (iOS). Permission is requested the first time the user enables alerts in Settings. Notification content contains only severity and general area — no victim details.',
  },
];

export default function AppReview() {
  const [openStep, setOpenStep] = useState(null);
  const [openPrivacy, setOpenPrivacy] = useState(null);
  const [copied, setCopied] = useState('');

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    });
  };

  return (
    <div className="ar-page">
      <header className="ar-header">
        <Link to="/" className="ar-back">← SafeHer</Link>
        <div className="ar-badge">🍎 Apple App Review</div>
        <h1>App Review Guide</h1>
        <p>Test credentials, feature walkthrough, and privacy architecture notes for Apple reviewers</p>
      </header>

      <div className="ar-content">

        {/* Notice */}
        <div className="ar-notice">
          <span className="ar-notice-icon">ℹ️</span>
          <div>
            <strong>This page is for Apple App Store reviewers.</strong> It provides test accounts, a step-by-step feature walkthrough, and an explanation of how anonymous reporting works. It is not linked from the main navigation.
          </div>
        </div>

        {/* Test Accounts */}
        <section className="ar-section">
          <h2 className="ar-section-title">Test Accounts</h2>
          <p className="ar-section-sub">All accounts are pre-seeded on the live backend. No sign-up required.</p>
          <div className="ar-accounts">
            {testAccounts.map((a) => (
              <div key={a.role} className="ar-account-card" style={{ borderTop: `4px solid ${a.color}` }}>
                <div className="ar-account-role" style={{ color: a.color }}>{a.role}</div>
                <div className="ar-cred-row">
                  <span className="ar-cred-label">Email</span>
                  <span className="ar-cred-val">{a.email}</span>
                  <button
                    className="ar-copy-btn"
                    onClick={() => copy(a.email, a.role + 'email')}
                  >{copied === a.role + 'email' ? '✓' : 'Copy'}</button>
                </div>
                <div className="ar-cred-row">
                  <span className="ar-cred-label">Password</span>
                  <span className="ar-cred-val">{a.password}</span>
                  <button
                    className="ar-copy-btn"
                    onClick={() => copy(a.password, a.role + 'pw')}
                  >{copied === a.role + 'pw' ? '✓' : 'Copy'}</button>
                </div>
                <p className="ar-account-note">{a.note}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Walkthrough */}
        <section className="ar-section">
          <h2 className="ar-section-title">Feature Walkthrough</h2>
          <p className="ar-section-sub">Tap each step to expand instructions.</p>
          <div className="ar-steps">
            {walkthrough.map((step, i) => (
              <div
                key={i}
                className={`ar-step ${openStep === i ? 'ar-step--open' : ''}`}
                onClick={() => setOpenStep(openStep === i ? null : i)}
              >
                <div className="ar-step-header">
                  <span className="ar-step-icon">{step.icon}</span>
                  <span className="ar-step-num">Step {i + 1}</span>
                  <span className="ar-step-title">{step.title}</span>
                  <span className="ar-step-chevron">{openStep === i ? '▲' : '▼'}</span>
                </div>
                {openStep === i && (
                  <div className="ar-step-body">{step.body}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section className="ar-section">
          <h2 className="ar-section-title">🔒 Privacy & Permissions Explained</h2>
          <p className="ar-section-sub">Notes on permissions requested and how user data is handled.</p>
          <div className="ar-privacy-list">
            {privacyPoints.map((p, i) => (
              <div
                key={i}
                className={`ar-step ${openPrivacy === i ? 'ar-step--open' : ''}`}
                onClick={() => setOpenPrivacy(openPrivacy === i ? null : i)}
              >
                <div className="ar-step-header">
                  <span className="ar-step-title" style={{ paddingLeft: '0.2rem' }}>{p.title}</span>
                  <span className="ar-step-chevron">{openPrivacy === i ? '▲' : '▼'}</span>
                </div>
                {openPrivacy === i && (
                  <div className="ar-step-body">{p.body}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Permissions table */}
        <section className="ar-section">
          <h2 className="ar-section-title">iOS Permissions Requested</h2>
          <div className="ar-perms">
            <div className="ar-perm-row ar-perm-header">
              <span>Permission</span>
              <span>When requested</span>
              <span>Purpose</span>
            </div>
            <div className="ar-perm-row">
              <span>Location (When In Use)</span>
              <span>First map load</span>
              <span>Center map on user; compute proximity alerts</span>
            </div>
            <div className="ar-perm-row">
              <span>Push Notifications</span>
              <span>When user enables alerts in Settings</span>
              <span>Proximity alert delivery via APNs</span>
            </div>
          </div>
          <p className="ar-perm-note">No camera, microphone, contacts, or background location access is requested.</p>
        </section>

        {/* Links */}
        <section className="ar-section ar-links-section">
          <h2 className="ar-section-title">Additional Resources</h2>
          <div className="ar-links">
            <Link to="/how-it-works" className="ar-link-card">
              <span className="ar-link-icon">📖</span>
              <span>How SafeHer Works</span>
            </Link>
            <Link to="/privacy" className="ar-link-card">
              <span className="ar-link-icon">🔒</span>
              <span>Privacy Policy</span>
            </Link>
            <a href="mailto:support@safeher.xyz" className="ar-link-card">
              <span className="ar-link-icon">✉️</span>
              <span>Contact Support</span>
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
